import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '../lib/api'
import { Brand, ThemeToggle } from '../components/Bits'
import { useAuth } from '../lib/auth'
import { useSeo } from '../lib/seo'
import { people, when } from '../lib/plural'

/** Зеркало итогов из снапшота (ru.clapme.snapshot.SnapshotService.build). */
interface SnapshotSlide {
  id: string
  kind: string
  title: string
  answered: number
  bars: { optionId: string; text: string; count: number; correct: boolean }[]
  texts: { text: string; count: number }[]
  emoji: { emoji: string; count: number }[]
}

interface Snapshot {
  title: string
  createdAt: string
  endedAt: string | null
  participants: unknown[]
  leaderboard: { name: string; score: number }[]
  slides: SnapshotSlide[]
}

/**
 * Итоги одного запуска — страница для владельца демонстрации. Читает тот же снапшот,
 * что писался весь эфир, поэтому здесь всё, что было на экранах: вопросы,
 * распределение голосов и лидеры.
 */
export default function Results() {
  const { code = '' } = useParams()
  const { ready, user } = useAuth()
  const [data, setData] = useState<Snapshot | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  useSeo({ title: 'Итоги запуска — clapme', noindex: true })

  useEffect(() => {
    if (!ready) return
    if (!user) {
      setMessage('Итоги доступны после входа — они принадлежат владельцу демонстрации.')
      return
    }
    api.runs
      .results(code)
      .then((raw) => setData(raw as unknown as Snapshot))
      .catch((e) => setMessage(e instanceof Error ? e.message : 'Итоги не получились'))
  }, [ready, user, code])

  return (
    <div className="wrap col" style={{ gap: 20, maxWidth: 900 }}>
      <header className="row spread row-wrap">
        <Brand />
        <ThemeToggle />
      </header>

      {message && (
        <div className="card col">
          <h2 style={{ margin: 0 }}>Итогов нет</h2>
          <p className="muted" style={{ margin: 0 }}>
            {message}
          </p>
        </div>
      )}

      {data && (
        <>
          <div className="card col" style={{ gap: 6 }}>
            <h1 style={{ margin: 0, fontSize: 28 }}>{data.title}</h1>
            <span className="muted tiny">
              запуск {when(data.createdAt)}
              {data.endedAt ? `, закончен ${when(data.endedAt)}` : ', не завершён'} ·{' '}
              {people(data.participants.length)}
            </span>
          </div>

          {data.leaderboard.length > 0 && (
            <div className="card col">
              <h3 className="card-title">Лидеры</h3>
              <div className="leaders">
                {data.leaderboard.slice(0, 10).map((row, index) => (
                  <div className={`leader${index === 0 ? ' leader-1' : ''}`} key={`${row.name}-${index}`}>
                    <span className="leader-place">{index + 1}</span>
                    <span>{row.name}</span>
                    <span className="leader-score">{row.score}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.slides.map((slide, index) => {
            const max = Math.max(1, ...slide.bars.map((bar) => bar.count))
            return (
              <div className="card col" key={slide.id} style={{ gap: 10 }}>
                <div className="row spread">
                  <h3 className="card-title" style={{ margin: 0 }}>
                    {index + 1}. {slide.title}
                  </h3>
                  <span className="muted tiny">ответили: {slide.answered}</span>
                </div>

                {slide.bars.length > 0 && (
                  <div className="col" style={{ gap: 6 }}>
                    {slide.bars.map((bar) => (
                      <div className="row" key={bar.optionId} style={{ gap: 10, alignItems: 'center' }}>
                        <span style={{ minWidth: 180 }}>
                          {bar.text}
                          {bar.correct && <span title="верный ответ"> ✓</span>}
                        </span>
                        <div className="collect-track grow">
                          <i style={{ width: `${Math.round((bar.count / max) * 100)}%` }} />
                        </div>
                        <span className="muted tiny" style={{ minWidth: 32, textAlign: 'right' }}>
                          {bar.count}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {slide.texts.length > 0 && (
                  <div className="row row-wrap" style={{ gap: 8 }}>
                    {slide.texts.map((item) => (
                      <span className="badge" key={item.text}>
                        {item.text}
                        {item.count > 1 && <span className="muted"> ×{item.count}</span>}
                      </span>
                    ))}
                  </div>
                )}

                {slide.emoji.length > 0 && (
                  <div className="row row-wrap" style={{ gap: 8 }}>
                    {slide.emoji.map((item) => (
                      <span className="badge" key={item.emoji}>
                        {item.emoji} <strong>{item.count}</strong>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </>
      )}
    </div>
  )
}
